import {Component, computed, inject, OnInit, signal} from '@angular/core';
import {AppAlertService, ParametersService, SubtitlesService, ZonesSettingsService} from "../core";
import {NgOptimizedImage} from "@angular/common";
import {LoaderComponent} from "../common/loader/loader.component";
import {ZonesListItemComponent} from "./zones-list-item/zones-list-item.component";
import {AppDialogComponent} from "../common/app-dialog/app-dialog.component";
import {ZoneSettingsDialogComponent} from "./zone-settings-dialog/zone-settings-dialog.component";
import {ChangeSettingsDialogComponent} from "./change-settings-dialog/change-settings-dialog.component";
import {ActionType, ContainersListItem, ZoneState, ZonesListItem} from "../models";

@Component({
    selector: 'zones-settings',
    standalone: true,
    imports: [NgOptimizedImage, LoaderComponent, ZonesListItemComponent, AppDialogComponent, ZoneSettingsDialogComponent, ChangeSettingsDialogComponent],
    templateUrl: './zones-settings.component.html',
    styleUrl: './zones-settings.component.scss'
})
export class ZonesSettingsComponent implements OnInit {
    private appAlert = inject(AppAlertService);
    private parameters = inject(ParametersService);
    private subtitlesService = inject(SubtitlesService);
    private zonesSettingsService = inject(ZonesSettingsService);

    dialogView = signal<ActionType | 'settings' | 'closed'>('closed');
    selectedZone = signal<ZonesListItem | null>(null);

    isRequesting = computed(() => this.zonesSettingsService.isRequesting());
    isSystemController = computed(() => this.parameters.isZonesSystemControllerType());
    selectedAction = computed<ActionType | null>(() => {
        const dialog = this.dialogView();
        return dialog !== 'settings' && dialog !== 'closed' ? dialog : null;
    });
    zonesList = computed(() => this.zonesSettingsService.zonesList().map((zone) => this.withStatusText(zone)));
    containersList = computed<ContainersListItem[]>(() => this.zonesSettingsService.containersList().map((container) => ({
        ...container,
        zonesList: container.zonesList.map((zone) => this.withStatusText(zone))
    })));
    showUpdatingState = computed(() => !this.isRequesting() && this.zonesList().length === 0 && this.containersList().length === 0);
    showZonesList = computed(() => !this.isRequesting() && !this.isSystemController());
    showContainersList = computed(() => !this.isRequesting() && this.isSystemController());
    showSettingsDialog = computed(() => this.dialogView() === 'settings');
    changeDialogData = computed<{ action: ActionType; zone: ZonesListItem } | null>(() => {
        const action = this.selectedAction();
        const zone = this.selectedZone();
        return action !== null && zone !== null ? {action, zone} : null;
    });

    protected labels = {
        zones: this.subtitlesService.setSubtitle(1752),
        updating: `${this.subtitlesService.setSubtitle(1326)}. ${this.subtitlesService.setSubtitle(624)}`,
        noCommunication: this.subtitlesService.setSubtitle(1808),
        noZones: this.subtitlesService.setSubtitle(2433),
        settings: this.subtitlesService.setSubtitle(845),
        zoneOff: this.subtitlesService.setSubtitle(922),
        zoneUnregistered: this.subtitlesService.setSubtitle(2310),
        sensorDamaged: this.subtitlesService.setSubtitle(1807),
        active: this.subtitlesService.setSubtitle(2792),
        errorText: this.subtitlesService.setSubtitle(1941),
        ok: this.subtitlesService.setSubtitle(475),
    };

    ngOnInit() {
        this.zonesSettingsService.getZonesList().subscribe({
            error: () => this.appAlert.openAlertDialog({
                title: this.labels.zones,
                message: this.labels.errorText,
                okButtonText: this.labels.ok,
            }),
        });
    }

    openZoneDialog(zone: ZonesListItem): void {
        this.selectedZone.set(zone);
        this.dialogView.set('settings');
    }

    openActionDialog(action: ActionType): void {
        this.dialogView.set(action);
    }

    closeDialog(): void {
        this.dialogView.set('closed');
        this.selectedZone.set(null);
    }

    goBackToSettingsDialog(): void {
        this.dialogView.set('settings');
    }

    private withStatusText(zone: ZonesListItem): ZonesListItem {
        return {
            ...zone,
            statusText: this.getZoneStatus(zone.status),
        };
    }

    private getZoneStatus(status: ZoneState): string {
        switch (status) {
            case 'zoneOff':
                return this.labels.zoneOff;
            case 'zoneUnregistered':
                return this.labels.zoneUnregistered;
            case 'sensorDamaged':
                return this.labels.sensorDamaged;
            case 'noCommunication':
                return this.labels.noCommunication;
            case 'noAlarm':
                return this.labels.active;
        }
    }
}
